import type {
  CashDrawerLocation,
  CashDrawerWorkstation,
} from '@/lib/services/cash-drawers/cash-drawers.types'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Typography } from '@/components/ui/typography'
import { useIsReadOnly } from '@/lib/casl'
import { serverFieldErrors, submitSchemaValidator, useAppForm } from '@/lib/form'
import {
  useCreateCashDrawerWorkstationMutation,
  useUpdateCashDrawerWorkstationMutation,
} from '@/lib/services/cash-drawers/cash-drawers.query'
import { cashDrawerWorkstationSchema } from '@/lib/services/cash-drawers/cash-drawers.schema'
import { getErrorMessage } from '@/lib/utils'
import { Pencil, Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { getLocationName } from '@/lib/services/locations/locations.utils'

type RegisterLocation = Pick<CashDrawerLocation, 'id' | 'is_active' | 'is_deleted' | 'address'>
export function CashDrawerRegisterSettings({
  workstations,
  locations,
}: {
  workstations: CashDrawerWorkstation[]
  locations: RegisterLocation[]
}) {
  const { isReadOnly } = useIsReadOnly()
  const updateMutation = useUpdateCashDrawerWorkstationMutation()
  const [editingWorkstation, setEditingWorkstation] = useState<CashDrawerWorkstation | null>(null)
  const [isAdding, setIsAdding] = useState(false)

  const changeActiveState = async (workstation: CashDrawerWorkstation) => {
    try {
      await updateMutation.mutateAsync({
        workstationId: workstation.id,
        is_active: !workstation.is_active,
      })
      toast.success(workstation.is_active ? 'Register deactivated' : 'Register activated')
    } catch (error) {
      toast.error('Register was not changed', { description: getErrorMessage(error) })
    }
  }

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-4">
          <div className="space-y-1.5">
            <CardTitle>Registers</CardTitle>
            <CardDescription>
              Create one register for each physical checkout station. Then, on each station, use Set
              up register in the Sales Orders header to pair that browser.
            </CardDescription>
          </div>
          {!isReadOnly ? (
            <Button type="button" size="sm" onClick={() => setIsAdding(true)}>
              <Plus className="size-4" /> Add register
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          {workstations.length === 0 ? (
            <Typography variant="supporting" tone="muted">
              No registers have been set up yet.
            </Typography>
          ) : (
            <div className="divide-y rounded-lg border">
              {workstations.map((workstation) => (
                <div
                  key={workstation.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="min-w-0">
                    <Typography variant="labelStrong" className="block truncate">
                      {workstation.name}
                    </Typography>
                    <Typography variant="supporting" tone="muted" className="block">
                      {workstation.location.name}
                      {!workstation.is_active ? ' · Inactive' : ''}
                      {!workstation.location.is_active ? ' · Location unavailable' : ''}
                      {workstation.has_open_session ? ' · Cash drawer in use' : ''}
                    </Typography>
                  </div>
                  {!isReadOnly ? (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!workstation.location.is_active}
                        onClick={() => setEditingWorkstation(workstation)}
                      >
                        <Pencil className="size-4" /> Edit
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={
                          updateMutation.isPending ||
                          workstation.has_open_session ||
                          !workstation.location.is_active
                        }
                        onClick={() => changeActiveState(workstation)}
                      >
                        {workstation.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {isAdding ? <RegisterDialog open locations={locations} onOpenChange={setIsAdding} /> : null}
      {editingWorkstation ? (
        <RegisterDialog
          key={editingWorkstation.id}
          open
          workstation={editingWorkstation}
          locations={locations}
          onOpenChange={(open) => !open && setEditingWorkstation(null)}
        />
      ) : null}
    </>
  )
}

function RegisterDialog({
  open,
  onOpenChange,
  workstation,
  locations,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  workstation?: CashDrawerWorkstation
  locations: RegisterLocation[]
}) {
  const createMutation = useCreateCashDrawerWorkstationMutation()
  const updateMutation = useUpdateCashDrawerWorkstationMutation()
  const mutation = workstation ? updateMutation : createMutation
  const form = useAppForm({
    defaultValues: {
      name: workstation?.name ?? '',
      location_id:
        workstation?.location?.id ??
        locations.find((location) => location.is_active && !location.is_deleted)?.id ??
        '',
      is_active: workstation?.is_active ?? true,
    },
    validators: {
      onSubmit: submitSchemaValidator(cashDrawerWorkstationSchema),
      onSubmitAsync: async ({ value }) => {
        try {
          await (workstation
            ? updateMutation.mutateAsync({
                workstationId: workstation.id,
                name: value.name,
                location_id: Number(value.location_id),
              })
            : createMutation.mutateAsync({
                is_active: value.is_active,
                name: value.name,
                location_id: Number(value.location_id),
              }))
          toast.success(workstation ? 'Register updated' : 'Register added')
          onOpenChange(false)
        } catch (error) {
          toast.error('Register was not saved', { description: getErrorMessage(error) })
          return serverFieldErrors(error)
        }
        return undefined
      },
    },
  })
  const locationOptions = locations
    .filter(
      (location) =>
        (location.is_active && !location.is_deleted) ||
        Number(location.id) === Number(workstation?.location?.id)
    )
    .map((location) => ({
      id: location.id,
      name: getLocationName(location),
    }))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{workstation ? 'Edit register' : 'Add register'}</DialogTitle>
          <DialogDescription>
            Give the register a name staff will recognize when choosing a register.
          </DialogDescription>
        </DialogHeader>
        <form.AppForm>
          <form.Form className="space-y-4">
            <form.AppField name="name">
              {(field) => <field.TextInput label="Register name" placeholder="Front Counter" />}
            </form.AppField>
            <form.AppField name="location_id">
              {(field) => <field.SelectInput label="Location" options={locationOptions} />}
            </form.AppField>
            <form.SubmitButton mutation={mutation} disabled={locationOptions.length === 0}>
              {workstation ? 'Save register' : 'Add register'}
            </form.SubmitButton>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  )
}
